<script lang="ts" setup>
import { computed, defineAsyncComponent } from 'vue';
import { vAnchor } from '@dh-io-owpi/shared/src/directives/anchor.directive';
import { vAemReady } from '@dh-io-owpi/shared/src/directives/aem-ready.directive';
import type { ComponentData } from './lib/types/aem';
import { useAemData } from '@dh-io-owpi/shared/src/plugins/aemData';
import { initializeApi } from '@dh-io-owpi/backend-api';
import { getApiBaseUrl } from '@dh-io-owpi/shared/src/lib/configuration';

// Composables
import { useVehicleFeatures } from './composables/useVehicleFeatures';

// Components
import VehicleFeatures from './VehicleFeatures.vue';
import { ErrorList } from '@dh-io-owpi/shared/src/components/ErrorList';
const AemAuthorDefaultBox = defineAsyncComponent(
  () => import('./components/AemAuthorDefaultBox/AemAuthorDefaultBox.vue'),
);
const HiddenNotification = defineAsyncComponent(() => import('./components/HiddenNotification/HiddenNotification.vue'));

const props = defineProps<{
  showHighlights?: boolean;
}>();

const aemData = useAemData<ComponentData>();

initializeApi(getApiBaseUrl(aemData));

const { vehicleFeaturesData, errorMessage, equipmentIdErrorLabels } = useVehicleFeatures(
  aemData,
  !!props.showHighlights,
);

const isAemDataValid = computed(() => !!aemData.featureElements?.length);
const isHidden = computed(() => !!aemData.isHidden);
const darkMode = computed(() => !!aemData.isDarkMode);
const anchorId = computed(() => aemData.anchorId);
const hasErrors = computed(() => !!(equipmentIdErrorLabels.value.length || errorMessage.value));
const showNotificationWrapper = computed(() => isHidden.value || hasErrors.value);
</script>

<template>
  <div
    v-anchor="anchorId"
    v-aem-ready="aemData"
    :class="['owpi-vehicle-features-wrapper', { 'owpi-vehicle-features-wrapper--dark': darkMode }]"
  >
    <slot />
    <div
      v-if="aemData.isAemEditMode && showNotificationWrapper"
      class="owpi-vehicle-features-wrapper__notification-wrapper"
    >
      <HiddenNotification v-if="isHidden" class="owpi-vehicle-features-wrapper__hidden-notification" />
      <ErrorList
        v-if="hasErrors"
        class="owpi-vehicle-features-wrapper__error-list"
        :errors="errorMessage ? [errorMessage] : equipmentIdErrorLabels"
      />
    </div>
    <div class="owpi-vehicle-features-wrapper__text-block">
      <p class="owpi-vehicle-features-wrapper__subtitle">{{ aemData.subtitle }}</p>
      <h2 class="owpi-vehicle-features-wrapper__headline">{{ aemData.headline }}</h2>
    </div>

    <VehicleFeatures
      v-if="(showHighlights || isAemDataValid) && (!isHidden || aemData.isAemEditMode)"
      :aemData="aemData"
      :darkMode="darkMode"
      :vehicleFeaturesData="vehicleFeaturesData"
      :analyticsSectionName="aemData.aemModulename || 'vehicle_features'"
    />

    <AemAuthorDefaultBox
      v-else-if="aemData.isAemEditMode && !showNotificationWrapper"
      headline="Vehicle features module (dynamic)"
      description="- not configured yet -"
    />
  </div>
</template>

<style lang="scss">
// styles to show while the component is not ready
// may need some adjustments, we should probably add some SCSS variables for the sizes
.owpi-vehicle-features-wrapper:not(.owpi-aem-ready) {
  display: block;
  min-height: var(--vf-calculated-height);
}

.owpi-vehicle-features-wrapper {
  &--dark {
    background-color: black;
  }

  &__text-block {
    @include wb-content-width;
    @include wb-spacing('padding', 'xl 0 m');
  }

  &__subtitle {
    @include wb-type-copy-secondary;
    @include wb-spacing('margin-bottom', 'xxs');

    color: var(--wb-grey-45);

    .owpi-vehicle-features-wrapper--dark & {
      color: var(--wb-grey-70);
    }
  }

  &__headline {
    @include wb-type-heading-l;

    color: var(--wb-grey-20);

    .owpi-vehicle-features-wrapper--dark & {
      color: var(--wb-white);
    }
  }

  &__notification-wrapper {
    @include wb-content-width;
    @include wb-spacing('padding-top', 'xl');

    &:only-child {
      @include wb-spacing('padding-bottom', 'xl');
    }
  }

  &__error-list {
    &:not(:first-child) {
      @include wb-spacing('margin-top', 'xs');
    }
  }
}
</style>
